import React from 'react';
import { useState } from 'react'; // <-- Import useState
import toast from 'react-hot-toast';
import { FiDownload, FiEye, FiLoader } from 'react-icons/fi';

import { DataState } from '../../context/DataProvider'; // <-- Import DataState
import { API_URL } from '../../services/api'; // <-- Import downloadFile service
import PreviewModal from './PreviewModal'; // Import the modal
import ExcelPreviewModal from './modals/ExcelPreviewModal'; // Import Excel modal
import { getFileIcon } from './AttachmentIcon';
import { clearLocalStorageKeepingLoginHint } from '../../config/authStorageKeys';

// +++ NEW: Fetches the raw file content for previewing +++
const getFileForPreview = async (accessToken, fileId) => {
  const options = {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
    credentials: 'include',
  };

  try {
    const response = await fetch(
      `${API_URL}/file/download-direct/${fileId}`,
      options,
    );

    if (response.status === 401) {
      clearLocalStorageKeepingLoginHint();
      window.location.href = '/login';
      return;
    }

    if (!response.ok) {
      throw new Error(`Error fetching file: ${response.statusText}`);
    }

    // Return the response as a blob
    return response.blob();
  } catch (error) {
    throw error;
  }
};

const MessageAttachments = ({ attachments, lightTheme = false }) => {
  // --- 1. ADD STATE & HANDLERS (No UI impact) ---
  const { userGoogleId } = DataState();
  const [processingFileId, setProcessingFileId] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [excelPreviewModal, setExcelPreviewModal] = useState({
    isOpen: false,
    file: null,
  });

  const handleDownload = async (file) => {
    setProcessingFileId(file.file_id);
    try {
      const blob = await getFileForPreview(userGoogleId, file.file_id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename || file.filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error downloading file.');
    } finally {
      setProcessingFileId(null);
    }
  };

  const handleExcelPreview = async (file) => {
    setProcessingFileId(file.file_id);
    try {
      const blob = await getFileForPreview(userGoogleId, file.file_id);
      const fileObject = new File([blob], file.original_filename, {
        type:
          blob.type ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      setExcelPreviewModal({ isOpen: true, file: fileObject });
    } catch (error) {
      toast.error('Could not load preview.');
    } finally {
      setProcessingFileId(null);
    }
  };

  const closeExcelPreview = () => {
    setExcelPreviewModal({ isOpen: false, file: null });
  };

  const handleDocxPreview = async (file) => {
    setProcessingFileId(file.file_id);
    try {
      const blob = await getFileForPreview(userGoogleId, file.file_id);
      const fileObject = new File([blob], file.original_filename, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      setPreviewFile(fileObject);
      setIsPreviewOpen(true);
    } catch (error) {
      toast.error('Could not load preview.');
    } finally {
      setProcessingFileId(null);
    }
  };

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <>
      {/* Modal is rendered but invisible until activated */}
      <PreviewModal
        file={previewFile}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />
      <ExcelPreviewModal
        isOpen={excelPreviewModal.isOpen}
        onClose={closeExcelPreview}
        file={excelPreviewModal.file}
      />

      <div className="relative mr-0 w-full" dir="rtl">
        <div className="no-scrollbar overflow-x-auto">
          <div className="flex items-end gap-2">
            {attachments.map((file) => {
              const fname = (
                file.original_filename ||
                file.filename ||
                ''
              ).toLowerCase();
              const isDocx = fname.endsWith('.docx');
              const isExcel =
                fname.endsWith('.xlsx') ||
                fname.endsWith('.xls') ||
                fname.endsWith('.csv');
              const isPreviewable = isDocx || isExcel;
              const isProcessingThisFile = processingFileId === file.file_id;
              const isAnyFileProcessing = processingFileId !== null;

              return (
                // --- 2. MINIMAL JSX CHANGES ---
                // The container now includes the 'group' class for hover effects
                <div
                  key={file.s3_filename || file.file_id}
                  className={`group flex flex-shrink-0 items-center gap-2 rounded-full px-3 py-1.5 text-xs transition-colors duration-200 ${
                    lightTheme
                      ? 'border border-zinc-600 bg-zinc-700 text-zinc-200'
                      : 'border border-zinc-700 bg-zinc-800 text-zinc-300'
                  }`}
                >
                  {/* Filename remains unchanged */}
                  <span className="font-medium">
                    {file.original_filename || file.filename}
                  </span>

                  {/* This container will hold either the icon or the button */}
                  <div className="relative flex h-4 w-4 items-center justify-center">
                    {/* Default Icon: Fades out on hover */}
                    <span className="transition-opacity duration-200 group-hover:opacity-0">
                      {getFileIcon(file.original_filename)}
                    </span>

                    {/* Hover Button: Fades in on hover, positioned over the icon */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                      {isPreviewable ? (
                        <button
                          onClick={() => {
                            if (isDocx) handleDocxPreview(file);
                            else if (isExcel) handleExcelPreview(file);
                          }}
                          disabled={isAnyFileProcessing}
                          className="disabled:cursor-not-allowed disabled:opacity-50"
                          title="Preview Document"
                        >
                          {isProcessingThisFile ? (
                            <FiLoader className="animate-spin" />
                          ) : (
                            <FiEye />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDownload(file)}
                          disabled={isAnyFileProcessing}
                          className="disabled:cursor-not-allowed disabled:opacity-50"
                          title="Download File"
                        >
                          {isProcessingThisFile ? (
                            <FiLoader className="animate-spin" />
                          ) : (
                            <FiDownload />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default MessageAttachments;
